import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Divider,
  Grid,
  CircularProgress,
} from '@mui/material'
import { Print, ArrowBack } from '@mui/icons-material'
import { Button, DocumentQRCode, PrintStyles, StatusChip, DocumentBrandHeader, PoweredByDuoNep } from '@components/common'
import { useDocumentRegistry } from '@hooks/useDocumentRegistry'
import { toast } from 'react-toastify'
import api from '@services/api'
import { useAuth } from '@context/AuthContext'
import { formatDate, formatDateTime } from '@utils/dateUtils'

const AssetDetailPrint = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [asset, setAsset] = useState(null)
  const [loading, setLoading] = useState(true)
  const { registerDocument, verificationCode, loading: registering } = useDocumentRegistry()

  useEffect(() => {
    const fetchAsset = async () => {
      try {
        const response = await api.get(`/assets/assets/${id}/`)
        setAsset(response.data)
      } catch (error) {
        console.error('Error fetching asset:', error)
        toast.error('Failed to load asset')
        navigate('/assets')
      } finally {
        setLoading(false)
      }
    }
    fetchAsset()
  }, [id, navigate])

  // Set document title for meaningful PDF filename
  useEffect(() => {
    if (asset) {
      document.title = `Asset Details - ${asset.asset_tag || asset.name} - DuoNep`
    }
    return () => {
      document.title = 'DuoNep'
    }
  }, [asset])

  // Generate document number with timestamp
  const generateDocumentNumber = () => {
    const now = new Date()
    const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '')
    const timeStr = now.toTimeString().slice(0, 5).replace(':', '')
    const assetRef = asset.asset_tag || asset.name
    return `${assetRef} - ${dateStr}-${timeStr}`
  }

  // Register document when asset is loaded
  useEffect(() => {
    if (asset && !verificationCode) {
      const documentNumber = generateDocumentNumber()
      registerDocument('asset_details', documentNumber, parseInt(id), asset)
        .catch((err) => console.error('Failed to register document:', err))
    }
  }, [asset, id, verificationCode, registerDocument])

  const handlePrint = async () => {
    // Ensure document is registered before printing
    if (!verificationCode && asset) {
      try {
        const documentNumber = generateDocumentNumber()
        await registerDocument('asset_details', documentNumber, parseInt(id), asset)
      } catch (err) {
        console.error('Registration failed:', err)
      }
    }
    window.print()
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!asset) {
    return null
  }

  return (
    <>
      {/* Print Controls - Hidden when printing */}
      <Box className="print-controls no-print" sx={{ p: 2, display: 'flex', gap: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          variant="outlined"
          onClick={() => navigate(`/assets/${id}`)}
        >
          Back
        </Button>
        <Button
          startIcon={<Print />}
          onClick={handlePrint}
          disabled={registering}
        >
          {registering ? 'Preparing...' : 'Print'}
        </Button>
      </Box>

      {/* Printable Content */}
      <Box className="print-content" sx={{ bgcolor: 'white' }}>
        {/* Co-branded document header */}
        <DocumentBrandHeader title="ASSET DETAILS" />

        {/* Asset Name and Tag */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="body1">
            <strong>Asset Name:</strong> {asset.name}
          </Typography>
          <Typography variant="body1">
            <strong>Asset Tag:</strong> {asset.asset_tag || 'N/A'}
          </Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* General Information */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          GENERAL INFORMATION
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Status</TableCell>
                <TableCell sx={{ width: '25%' }}>{asset.status || '-'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Criticality</TableCell>
                <TableCell sx={{ width: '25%' }}>{asset.criticality || '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                <TableCell>{asset.category?.name || '-'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Section/Unit</TableCell>
                <TableCell>{asset.department_name || '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Facility</TableCell>
                <TableCell>{asset.facility_name || '-'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Number of Units</TableCell>
                <TableCell>{asset.unit_count || 1}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Technical Details */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          TECHNICAL DETAILS
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Manufacturer</TableCell>
                <TableCell sx={{ width: '25%' }}>{asset.manufacturer?.name || '-'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Model Number</TableCell>
                <TableCell sx={{ width: '25%' }}>{asset.model_number || '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Serial Number</TableCell>
                <TableCell>{asset.serial_number || '-'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Engraved No / Asset Number</TableCell>
                <TableCell>{asset.engraved_no || '-'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Location */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          LOCATION
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Location</TableCell>
                <TableCell sx={{ width: '25%' }}>{asset.location?.name || '-'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Room Number</TableCell>
                <TableCell sx={{ width: '25%' }}>{asset.room_number || '-'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Purchase & Warranty */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          PURCHASE & WARRANTY
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Purchase Date</TableCell>
                <TableCell sx={{ width: '25%' }}>{formatDate(asset.purchase_date) || '-'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Purchase Cost</TableCell>
                <TableCell sx={{ width: '25%' }}>
                  {asset.purchase_cost ? `UGX ${Number(asset.purchase_cost).toLocaleString()}` : '-'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Warranty Expiry</TableCell>
                <TableCell>{formatDate(asset.warranty_expiry) || '-'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Expected Life</TableCell>
                <TableCell>{asset.expected_life_years ? `${asset.expected_life_years} years` : '-'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Manufacturing & Origin */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          MANUFACTURING & ORIGIN
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Manufacturing Date</TableCell>
                <TableCell sx={{ width: '25%' }}>{formatDate(asset.manufacturing_date) || '-'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Country of Origin</TableCell>
                <TableCell sx={{ width: '25%' }}>{asset.country_of_origin || '-'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Receiving Information */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          RECEIVING INFORMATION
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Supplier</TableCell>
                <TableCell sx={{ width: '25%' }}>{asset.supplier_name || '-'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Delivered By</TableCell>
                <TableCell sx={{ width: '25%' }}>{asset.delivered_by || '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Received By</TableCell>
                <TableCell>{asset.received_by_name || '-'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Received Date</TableCell>
                <TableCell>{formatDate(asset.received_date) || '-'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Remarks */}
        {asset.notes && (
          <>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              REMARKS
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 3, minHeight: 60 }}>
              <Typography variant="body2">{asset.notes}</Typography>
            </Paper>
          </>
        )}

        {/* QR Code Section */}
        {asset.qr_code && (
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              ASSET QR CODE
            </Typography>
            <Box
              component="img"
              src={asset.qr_code}
              alt="Asset QR Code"
              sx={{ width: 120, height: 120, objectFit: 'contain' }}
            />
            <Typography variant="caption" display="block" color="text.secondary">
              Scan for asset details
            </Typography>
          </Box>
        )}

        {/* Footer */}
        <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Printed: {formatDateTime(new Date())}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Printed by: {(user?.first_name || user?.last_name) ? `${user?.first_name || ''} ${user?.last_name || ''}`.trim() : user?.username || 'N/A'}
            </Typography>
          </Box>
          <DocumentQRCode
            verificationCode={verificationCode}
            documentType="Asset Details"
            documentNumber={asset?.asset_tag || asset?.name || ''}
            size={80}
          />
        </Box>

        {/* Powered by DuoNep */}
        <PoweredByDuoNep />
      </Box>

      <PrintStyles orientation="portrait" />
    </>
  )
}

export default AssetDetailPrint
